import { ConversationSession, QuickButton, SessionContext } from '../conversation/types';
import { CaseDetailsService } from '../caseDetails/caseDetailsService';
import { CaseMessageService } from '../caseMessages/types';
import { TriageCase, TriageService, TriageUser } from '../triage/types';
import { Personality } from './personalities';

export type PresetUser = TriageUser;

export interface PresetContext {
  user: PresetUser;
  session: ConversationSession;
  /** The voice drawn for this session; every conversational beat reads its wording from here. */
  personality: Personality;
  caseDetails: CaseDetailsService;
  triage: TriageService;
  /** Posts to a case's Messages tab, which its design team reads. */
  caseMessages: CaseMessageService;
  /** All presets, so handlers can build the menu. */
  presets: Preset[];
}

export interface PresetInput {
  /** The raw text the user typed, or the quick-button label. */
  text: string;
  /** Case number picked up from the text or a `case:` quick button. */
  caseRefNo?: string;
}

/** What a preset hands back to the engine. */
export interface BotTurn {
  messages: string[];
  quickButtons?: QuickButton[];
  /** Merged into the session context (e.g. set/clear `pendingIntentId`). */
  context?: Partial<SessionContext>;
  caseRefNo?: string;
}

export interface Preset {
  id: string;
  /** Quick-button text; also matched verbatim against user input. */
  label: string;
  showInMenu: boolean;
  /** Tie-break when several presets score the same. Higher wins. */
  priority: number;
  keywords: string[];
  patterns?: RegExp[];
  handler: (ctx: PresetContext, input: PresetInput) => Promise<BotTurn>;
}

export interface IntentMatch {
  presetId: string;
  score: number;
  caseRefNo?: string;
}

export const PRESET_BUTTON_PREFIX = 'preset:';
export const CASE_BUTTON_PREFIX = 'case:';
export const CASE_LOOKUP_PRESET_ID = 'case_lookup';
/** A case number with an ask that is not about status (a change, a fix): handed to the case's design team. */
export const CASE_QUESTION_PRESET_ID = 'case_question';
/** intentId on turns answered by the LLM API rather than a preset. */
export const LLM_INTENT_ID = 'llm';
/** intentId on turns where the dentist said it is an emergency: both ways to reach a person, ticket filed. */
export const EMERGENCY_INTENT_ID = 'emergency';

/** The case the user last looked at, in the shape triage/LLM payloads expect. */
export const selectedCaseFrom = (context: SessionContext): TriageCase | undefined =>
  context.selectedCaseRefNo ? { id: context.selectedCaseId, caseRefNo: context.selectedCaseRefNo } : undefined;
